import React, { useState, useEffect } from 'react';
import { 
  Plus, 
  Pencil, 
  Trash2, 
  Save, 
  X, 
  Image as ImageIcon,
  Calendar,
  Tag,
  Eye,
  RefreshCw
} from 'lucide-react';
import { useToast } from '../hooks/use-toast';

const API_URL = process.env.REACT_APP_BACKEND_URL;

const BlogAdmin = () => {
  const { toast } = useToast();
  const [blogs, setBlogs] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showEditor, setShowEditor] = useState(false);
  const [editingBlog, setEditingBlog] = useState(null);
  const [formData, setFormData] = useState({
    title: '',
    slug: '',
    excerpt: '',
    content: '',
    image: '',
    tags: ''
  });

  useEffect(() => {
    fetchBlogs();
  }, []);

  const fetchBlogs = async () => {
    try {
      setLoading(true);
      const response = await fetch(`${API_URL}/api/blogs`);
      if (!response.ok) throw new Error('Failed to fetch blogs');
      const data = await response.json();
      setBlogs(data);
    } catch (error) {
      console.error('Error fetching blogs:', error);
      toast({
        title: "Error",
        description: "Failed to load blog posts",
        variant: "destructive",
      });
    } finally {
      setLoading(false);
    }
  };

  const generateSlug = (title) => {
    return title
      .toLowerCase()
      .replace(/[^a-z0-9\s-]/g, '')
      .replace(/\s+/g, '-')
      .replace(/-+/g, '-')
      .trim();
  };

  const handleTitleChange = (e) => {
    const title = e.target.value;
    setFormData({
      ...formData,
      title,
      slug: generateSlug(title)
    });
  };

  const handleChange = (e) => {
    setFormData({
      ...formData,
      [e.target.name]: e.target.value
    });
  };

  const resetForm = () => {
    setFormData({
      title: '',
      slug: '',
      excerpt: '',
      content: '',
      image: '',
      tags: ''
    });
    setEditingBlog(null);
    setShowEditor(false);
  };

  const handleEdit = (blog) => {
    setFormData({
      title: blog.title,
      slug: blog.slug,
      excerpt: blog.excerpt,
      content: blog.content,
      image: blog.image,
      tags: blog.tags.join(', ')
    });
    setEditingBlog(blog);
    setShowEditor(true);
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    const blogData = {
      title: formData.title,
      slug: formData.slug,
      excerpt: formData.excerpt,
      content: formData.content,
      image: formData.image,
      tags: formData.tags.split(',').map(t => t.trim()).filter(t => t)
    };

    try {
      let response;
      if (editingBlog) {
        response = await fetch(`${API_URL}/api/blogs/${editingBlog.id}`, {
          method: 'PATCH',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify(blogData),
        });
      } else {
        response = await fetch(`${API_URL}/api/blogs`, {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify(blogData),
        });
      }

      if (!response.ok) throw new Error('Failed to save blog post');

      toast({
        title: editingBlog ? "Blog Updated" : "Blog Created",
        description: `"${formData.title}" has been ${editingBlog ? 'updated' : 'published'}`,
      });

      resetForm();
      fetchBlogs();
    } catch (error) {
      console.error('Error saving blog:', error);
      toast({
        title: "Error",
        description: "Failed to save blog post",
        variant: "destructive",
      });
    }
  };

  const handleDelete = async (blogId, title) => {
    if (!window.confirm(`Are you sure you want to delete "${title}"?`)) return;

    try {
      const response = await fetch(`${API_URL}/api/blogs/${blogId}`, {
        method: 'DELETE',
      });

      if (!response.ok) throw new Error('Failed to delete blog');

      setBlogs(prev => prev.filter(b => b.id !== blogId));
      toast({
        title: "Blog Deleted",
        description: `"${title}" has been removed`,
      });
    } catch (error) {
      console.error('Error deleting blog:', error);
      toast({
        title: "Error",
        description: "Failed to delete blog post",
        variant: "destructive",
      });
    }
  };

  return (
    <div className="blog-admin" data-testid="blog-admin">
      {/* Header */}
      <div className="blog-admin-header">
        <div>
          <h2 className="blog-admin-title">Blog Management</h2>
          <p className="blog-admin-subtitle">Create, edit, and manage blog posts</p>
        </div>
        <div className="blog-admin-actions">
          <button 
            onClick={fetchBlogs}
            className="admin-btn admin-btn-secondary"
          >
            <RefreshCw size={18} />
            Refresh
          </button>
          <button 
            onClick={() => setShowEditor(true)}
            className="admin-btn admin-btn-primary"
            data-testid="create-blog-btn"
          >
            <Plus size={18} />
            New Blog Post
          </button>
        </div>
      </div>

      {/* Editor Modal */}
      {showEditor && (
        <div className="blog-editor-overlay" data-testid="blog-editor">
          <div className="blog-editor-modal">
            <div className="blog-editor-header">
              <h3>{editingBlog ? 'Edit Blog Post' : 'Create New Blog Post'}</h3>
              <button onClick={resetForm} className="blog-editor-close">
                <X size={24} />
              </button>
            </div>

            <form onSubmit={handleSubmit} className="blog-editor-form">
              <div className="blog-form-row">
                <div className="blog-form-group">
                  <label htmlFor="title">Title *</label>
                  <input
                    type="text"
                    id="title"
                    name="title"
                    value={formData.title}
                    onChange={handleTitleChange}
                    required
                    placeholder="Enter blog title"
                    data-testid="blog-title-input"
                  />
                </div>
              </div>

              <div className="blog-form-row">
                <div className="blog-form-group">
                  <label htmlFor="slug">URL Slug</label>
                  <input
                    type="text"
                    id="slug"
                    name="slug"
                    value={formData.slug}
                    onChange={handleChange}
                    placeholder="auto-generated-from-title"
                  />
                </div>
              </div>

              <div className="blog-form-row">
                <div className="blog-form-group">
                  <label htmlFor="image">
                    <ImageIcon size={16} /> Header Image URL *
                  </label>
                  <input
                    type="url"
                    id="image"
                    name="image"
                    value={formData.image}
                    onChange={handleChange}
                    required
                    placeholder="https://example.com/image.jpg"
                    data-testid="blog-image-input"
                  />
                  {formData.image && (
                    <div className="blog-image-preview">
                      <img src={formData.image} alt="Preview" />
                    </div>
                  )}
                </div>
              </div>

              <div className="blog-form-row">
                <div className="blog-form-group">
                  <label htmlFor="excerpt">Excerpt / Summary *</label>
                  <textarea
                    id="excerpt"
                    name="excerpt"
                    value={formData.excerpt}
                    onChange={handleChange}
                    required
                    rows={2}
                    placeholder="Brief description shown on blog listing"
                    data-testid="blog-excerpt-input"
                  />
                </div>
              </div>

              <div className="blog-form-row">
                <div className="blog-form-group">
                  <label htmlFor="content">Content *</label>
                  <textarea
                    id="content"
                    name="content"
                    value={formData.content}
                    onChange={handleChange}
                    required
                    rows={10}
                    placeholder="Write your blog post content here. Use blank lines to separate paragraphs."
                    data-testid="blog-content-input"
                  />
                </div>
              </div>

              <div className="blog-form-row">
                <div className="blog-form-group">
                  <label htmlFor="tags">
                    <Tag size={16} /> Tags (comma-separated)
                  </label>
                  <input
                    type="text"
                    id="tags"
                    name="tags"
                    value={formData.tags}
                    onChange={handleChange}
                    placeholder="HairExtensions, Training, Beauty"
                  />
                </div>
              </div>

              <div className="blog-form-actions">
                <button type="button" onClick={resetForm} className="admin-btn admin-btn-secondary">
                  Cancel
                </button>
                <button type="submit" className="admin-btn admin-btn-primary" data-testid="save-blog-btn">
                  <Save size={18} />
                  {editingBlog ? 'Update Post' : 'Publish Post'}
                </button>
              </div>
            </form>
          </div>
        </div>
      )}

      {/* Blog List */}
      <div className="blog-list-container">
        {loading ? (
          <div className="loading-state">
            <RefreshCw className="animate-spin" size={32} />
            <p>Loading blog posts...</p>
          </div>
        ) : blogs.length === 0 ? (
          <div className="empty-state">
            <ImageIcon size={48} />
            <p>No blog posts yet</p>
            <button 
              onClick={() => setShowEditor(true)}
              className="admin-btn admin-btn-primary"
            >
              <Plus size={18} /> Create Your First Post
            </button>
          </div>
        ) : (
          <div className="blog-list">
            {blogs.map((blog) => (
              <div key={blog.id} className="blog-list-item" data-testid={`blog-item-${blog.id}`}>
                <div className="blog-list-image">
                  <img src={blog.image} alt={blog.title} />
                </div>
                <div className="blog-list-content">
                  <h3 className="blog-list-title">{blog.title}</h3>
                  <p className="blog-list-excerpt">{blog.excerpt}</p>
                  <div className="blog-list-meta">
                    <span className="blog-list-date">
                      <Calendar size={14} />
                      {new Date(blog.date).toLocaleDateString('en-GB', {
                        day: 'numeric',
                        month: 'short',
                        year: 'numeric'
                      })}
                    </span>
                    <span className="blog-list-tags">
                      {blog.tags.slice(0, 3).map((tag, i) => (
                        <span key={i} className="blog-mini-tag">#{tag}</span>
                      ))}
                    </span>
                  </div>
                </div>
                <div className="blog-list-actions">
                  <a 
                    href={`/blog/${blog.slug}`} 
                    target="_blank" 
                    rel="noopener noreferrer"
                    className="blog-action-btn view"
                    title="View post"
                  >
                    <Eye size={18} />
                  </a>
                  <button 
                    onClick={() => handleEdit(blog)}
                    className="blog-action-btn edit"
                    title="Edit post"
                    data-testid={`edit-blog-${blog.id}`}
                  >
                    <Pencil size={18} />
                  </button>
                  <button 
                    onClick={() => handleDelete(blog.id, blog.title)}
                    className="blog-action-btn delete"
                    title="Delete post"
                    data-testid={`delete-blog-${blog.id}`}
                  >
                    <Trash2 size={18} />
                  </button>
                </div>
              </div>
            ))}
          </div>
        )}
      </div>
    </div>
  );
};

export default BlogAdmin;
